import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasTableOfContents, headings, longestEmptyParagraphRun, readDocument } from "@/office/word/document";
import { hasField, readFields, readLayout } from "@/office/word/layout";

export const lektionSeitenlayoutAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: Ein druckfertiger Bericht",
  summary: "Gliederung, Umbrüche, Kopf- und Fußzeile in einem abgabefertigen Dokument.",
  durationMinutes: 12,
  learningGoals: [
    "Ein Dokument bis zur Druckreife aufbauen",
    "Die Seitenansicht zur Endkontrolle nutzen",
  ],
  steps: [
    {
      id: "druckfertig",
      title: "Den Bericht fertigstellen",
      explanation: [
        "Jetzt kommt alles zusammen: die Gliederung über Formatvorlagen, die " +
          "Absatzformatierung und das Seitenlayout aus diesem Modul.",
        "Bevor du prüfst, wirf einen Blick in die Seitenansicht über „Datei“ → „Drucken“. " +
          "Dort siehst du das Dokument genau so, wie es aus dem Drucker kommt – " +
          "einschließlich Kopf- und Fußzeile, die in der normalen Ansicht leicht blass " +
          "wirken.",
      ],
      keyPoints: [
        "Titel, Inhaltsverzeichnis und Kapitel auf getrennten Seiten.",
        "Kopfzeile mit Titel, Fußzeile mit Seitenzahl.",
        "Keine Leerzeilenketten mehr im Dokument.",
      ],
      task: {
        instruction:
          "Baue einen Bericht mit mindestens drei Überschriften, einem Inhaltsverzeichnis, " +
          "einer Kopfzeile mit Text, einer Fußzeile mit automatischer Seitenzahl und ohne " +
          "Leerzeilenketten.",
        hint: "Du kannst auf dem Dokument aus dem Formatvorlagen-Modul aufbauen und es um Kopf- und Fußzeile ergänzen.",
        check: async () => {
          const snapshot = await readDocument();
          const layout = await readLayout();
          const felder = await readFields();

          const ueberschriften = headings(snapshot);
          if (ueberschriften.length === 0) {
            return fail(
              "Im Dokument gibt es noch keine Überschriften.",
              "Weise zuerst Überschriftenvorlagen zu – ohne sie funktioniert auch das Inhaltsverzeichnis nicht.",
            );
          }

          const kriterien = [
            criterion(
              `Mindestens drei Überschriften (gefunden: ${ueberschriften.length})`,
              ueberschriften.length >= 3,
            ),
            criterion("Ein automatisches Inhaltsverzeichnis", hasTableOfContents(snapshot)),
            criterion("Die Kopfzeile enthält Text", layout.hasHeaderText),
            criterion("Die Fußzeile enthält Text", layout.hasFooterText),
            criterion(
              `Keine Leerzeilenketten (längste gefundene: ${longestEmptyParagraphRun(snapshot)})`,
              longestEmptyParagraphRun(snapshot) < 2,
            ),
          ];

          // Das Seitenzahl-Feld nur bewerten, wenn es sich auslesen lässt.
          if (felder !== null) {
            kriterien.push(
              criterion("Die Fußzeile enthält ein Seitenzahl-Feld", hasField(felder, "Page")),
            );
          }

          return fromCriteria(kriterien, {
            success:
              felder === null
                ? "Alles Prüfbare sitzt. Die Seitenzahl konnte diese Word-Version nicht melden – wirf zur Sicherheit einen Blick in die Seitenansicht."
                : "Sehr gut – dieser Bericht ist abgabefertig.",
            failure: "Fast geschafft. Diese Punkte fehlen noch:",
            hint: "Kontrolliere das Ergebnis in der Seitenansicht unter Datei → Drucken.",
          });
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Damit ist die dritte und letzte Formatierungsebene abgedeckt: Zeichen, Absatz, " +
          "Seite. Zusammen mit den Formatvorlagen hast du alles beisammen, um ein " +
          "mehrseitiges Dokument sauber aufzubauen.",
        "Auffällig ist erneut das Muster: Für jede Behelfslösung gibt es die richtige " +
          "Einstellung. Leerzeilen werden zum Seitenumbruch, getippte Seitenzahlen zum " +
          "Feld, wiederholte Kopfzeilen zur echten Kopfzeile.",
      ],
      keyPoints: [
        "Seitenumbruch mit Strg + Enter statt Leerzeilen.",
        "Abschnittsumbruch, wenn sich das Seitenlayout ändern soll.",
        "Kopf- und Fußzeile per Doppelklick in den Seitenrand.",
        "Seitenzahlen immer als Feld, nie getippt.",
      ],
    },
  ],
};
